import { Children, cloneElement, useEffect, useRef, useState } from "react";
import { Swiper } from "swiper/react";
import { EffectCoverflow } from "swiper/modules";

import arrowl_0 from '../../../../assets/img/utils/gallery-arrow-l-0.svg';
import arrowr_0 from '../../../../assets/img/utils/gallery-arrow-r-0.svg';
import arrowl_1 from '../../../../assets/img/utils/gallery-arrow-l-1.svg';
import arrowr_1 from '../../../../assets/img/utils/gallery-arrow-r-1.svg';

import "swiper/css";
import "swiper/css/pagination";

const arrowStyle = { height: "100px", margin: "auto", userSelect: 'none' };

const desktopQuery = "(min-width: 768px)";

// Two slides per view around a centered one: below six slides the loop runs out
// of slides to move to the front and leaves a blank gap at the edge.
const minLoopSlides = 6;

// Swiper does not re-apply effect settings when a breakpoint changes, so the two
// coverflow variants are picked here and the slider is rebuilt on the switch.
const useIsDesktop = () => {
    const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(desktopQuery).matches);

    useEffect(() => {
        const query = window.matchMedia(desktopQuery);
        const onChange = event => setIsDesktop(event.matches);
        query.addEventListener("change", onChange);
        setIsDesktop(query.matches);
        return () => query.removeEventListener("change", onChange);
    }, []);

    return isDesktop;
}

const ProjectGallerySwiper = ({ children }) => {
    const swiperRef = useRef(null);
    const prevImgRef = useRef(null);
    const nextImgRef = useRef(null);
    const isDesktop = useIsDesktop();
    const [activeIndex, setActiveIndex] = useState(0);

    const slides = Children.toArray(children);
    const loop = slides.length > 1;

    // Short galleries are repeated until there are enough slides to loop. Coming
    // back to the first photo after the last one is what looping means anyway, so
    // the copies read as the gallery coming round again.
    const passes = loop ? Math.ceil(minLoopSlides / slides.length) : 1;
    const loopedSlides = passes > 1
        ? Array.from({ length: passes }, (_, pass) =>
            slides.map(slide => cloneElement(slide, { key: `${pass}-${slide.key}` }))).flat()
        : slides;

    return (
        <div className="position-relative">
            {loop &&
                <div className="swiper-button-prev-n d-md-flex d-none"
                    style={{
                        height: "400px",
                        transform: "translate(-110%, 0)",
                        top: "0",
                        zIndex: "2"
                    }}
                    onClick={_ => swiperRef.current?.slidePrev()}
                    onMouseEnter={_ => { prevImgRef.current.src = arrowl_1 }}
                    onMouseLeave={_ => { prevImgRef.current.src = arrowl_0 }}
                >
                    <img src={arrowl_0} ref={prevImgRef} style={{ ...arrowStyle, paddingRight: "13.5rem" }} alt="" />
                </div>}
            <Swiper
                key={isDesktop ? "desktop" : "mobile"}
                effect={"coverflow"}
                grabCursor={true}
                centeredSlides={true}
                slidesPerView={2}
                coverflowEffect={isDesktop
                    ? { rotate: 0, stretch: 0, depth: 100, modifier: 2.5 }
                    : { rotate: 50, stretch: 0, depth: 20, modifier: 1 }}
                loop={loop}
                // Swiper clears its "animating" flag on transitionend. When that event
                // never arrives - animations switched off in the OS, the tab backgrounded
                // mid-slide - the flag stays set and the default of this option makes the
                // arrows dead from then on.
                loopPreventsSliding={false}
                // The arrows are wired below as plain onClick handlers on the swiper
                // instance instead of through the Navigation module: element/selector
                // binding proved fragile (document-wide selectors collide with other
                // sliders, and rebinding depends on mount timing).
                onSwiper={swiper => { swiperRef.current = swiper }}
                onSlideChange={swiper => setActiveIndex(swiper.realIndex % slides.length)}
                modules={[EffectCoverflow]}
                className="mySwiper project-gallery my-4"
            >
                {loopedSlides}
            </Swiper>
            {loop &&
                <div className="swiper-button-next-n d-md-flex d-none"
                    style={{
                        height: "400px",
                        transform: "translate(110%, 0)",
                        top: "0",
                        zIndex: "2"
                    }}
                    onClick={_ => swiperRef.current?.slideNext()}
                    onMouseEnter={_ => { nextImgRef.current.src = arrowr_1 }}
                    onMouseLeave={_ => { nextImgRef.current.src = arrowr_0 }}
                >
                    <img src={arrowr_0} ref={nextImgRef} style={{ ...arrowStyle, paddingLeft: "13.5rem" }} alt="" />
                </div>}
            {/* Rendered here rather than by Swiper so repeated slides do not add bullets. */}
            {loop &&
                <div className="swiper-pagination swiper-pagination-bullets swiper-pagination-horizontal">
                    {slides.map((slide, index) =>
                        <span key={slide.key}
                            className={"swiper-pagination-bullet" + (index === activeIndex ? " swiper-pagination-bullet-active" : "")} />
                    )}
                </div>
            }
        </div>
    );
}

export default ProjectGallerySwiper;
